import { Check, Copy, RotateCcw, Trash2, Webhook } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDestructiveDialog } from "@/components/ui/confirm-destructive-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SectionCard } from "@/components/ui/section-card";
import {
  useDeleteServiceUserWebhookSecret,
  useServiceUserWebhookSecret,
  useSetServiceUserWebhookSecret,
} from "@/features/service-users/hooks/useServiceUserWebhookSecret";

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

export function ServiceUserWebhookPanel({
  serviceUserId,
}: {
  serviceUserId: string;
}) {
  const { t } = useTranslation("serviceUsers");
  const secretQuery = useServiceUserWebhookSecret(serviceUserId);
  const setSecret = useSetServiceUserWebhookSecret(serviceUserId);
  const removeSecret = useDeleteServiceUserWebhookSecret(serviceUserId);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revealOpen, setRevealOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const data = secretQuery.data;
  const hasSecret = data?.has_secret ?? false;
  const rotatedAt = formatDate(data?.rotated_at ?? null);

  async function handleGenerate() {
    try {
      const created = await setSecret.mutateAsync();
      setRevealed(created.secret);
      setRevealOpen(true);
      toast.success(hasSecret ? t("toasts.secretRotated") : t("toasts.secretCreated"));
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleDelete() {
    try {
      await removeSecret.mutateAsync();
      toast.success(t("toasts.secretDeleted"));
      setDeleteOpen(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <SectionCard
      title={t("webhook.title")}
      description={t("webhook.description")}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line px-4 py-3">
        <span className="flex items-center gap-2 text-sm text-ink-950">
          <Webhook className="h-4 w-4 text-ink-500" />
          {hasSecret ? (
            <>
              <Badge variant="secondary">{t("webhook.configured")}</Badge>
              {rotatedAt ? (
                <span className="text-xs text-ink-500">
                  {t("webhook.rotatedAt")}: {rotatedAt}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-xs text-ink-500">{t("webhook.none")}</span>
          )}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleGenerate}
            disabled={setSecret.isPending}
          >
            <RotateCcw className="h-4 w-4" />
            {hasSecret ? t("webhook.rotate") : t("webhook.generate")}
          </Button>
          {hasSecret ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeleteOpen(true)}
              disabled={removeSecret.isPending}
            >
              <Trash2 className="h-4 w-4" />
              {t("webhook.delete")}
            </Button>
          ) : null}
        </div>
      </div>

      <WebhookSecretRevealDialog
        open={revealOpen}
        onOpenChange={setRevealOpen}
        secret={revealed}
      />

      <ConfirmDestructiveDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t("webhook.deleteConfirm.title")}
        description={t("webhook.deleteConfirm.description")}
        resourceName={t("webhook.deleteConfirm.resourceName")}
        resourceKind={t("webhook.deleteConfirm.resourceKind")}
        confirmLabel={t("webhook.deleteConfirm.confirm")}
        onConfirm={handleDelete}
        isPending={removeSecret.isPending}
      />
    </SectionCard>
  );
}

function WebhookSecretRevealDialog({
  open,
  onOpenChange,
  secret,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  secret: string | null;
}) {
  const { t } = useTranslation("serviceUsers");
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      toast.success(t("reveal.copied"));
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("reveal.copy"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("webhook.reveal.title")}</DialogTitle>
          <DialogDescription>
            {t("webhook.reveal.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Input
            value={secret ?? ""}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            className="font-mono text-xs"
          />
          <Button type="button" variant="outline" onClick={copy}>
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {t("reveal.copy")}
          </Button>
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t("reveal.done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
